'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import styles from './HomeDeviceView.module.css';
import gsap from 'gsap';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { usePreloadImages as useWarmCache } from 'hooks/dom/usePreloadImages';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { drawImageSafe } from 'utils/drawImageSafe';

const SEQUENCE_DIR = '/web_v2/uh-home-v2/sequences/home-device';
const START_INDEX = 0;
const END_INDEX = 202;
const FRAME_COUNT = END_INDEX - START_INDEX + 1;

const frameUrl = (idx: number) =>
  getCompressedAssetUrl(
    `${SEQUENCE_DIR}/Comp_${String(idx).padStart(5, '0')}.png`,
  );

const Container = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({ children, ...props }, ref) => (
  <section ref={ref} className={styles.container} {...props}>
    {children}
  </section>
));

Container.displayName = 'Container';

const Pill = React.forwardRef<
  HTMLSpanElement,
  { $bg?: string } & React.HTMLAttributes<HTMLSpanElement>
>(({ $bg, ...props }, ref) => (
  <span
    ref={ref}
    className={styles.pill}
    style={
      $bg
        ? ({ ['--pill-bg']: `url(${$bg})` } as React.CSSProperties)
        : undefined
    }
    {...props}
  />
));

Pill.displayName = 'Pill';

const HEADING_LINES = [
  'homeDeviceView.heading.line1',
  'homeDeviceView.heading.line2',
  'homeDeviceView.heading.line3',
] as const;

const HomeDeviceView = React.memo(() => {
  const t = useTranslations('uhHome');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLDivElement | null>(null);
  const pillsRef = useRef<HTMLSpanElement[]>([]);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const renderState = useRef({ frame: 0 });
  const lastDrawnFrameRef = useRef<number>(-1);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const [isReady, setIsReady] = useState(false);
  const gsapCtx = useGsapContext();
  const { add } = useMasterTimeline();

  const preloadUrls = useMemo(
    () =>
      Array.from({ length: FRAME_COUNT }, (_, i) => frameUrl(START_INDEX + i)),
    [],
  );
  useWarmCache(preloadUrls);

  const pillSrc = useMemo(
    () => ({
      home: getCompressedAssetUrl('/web_v2/uh-home-v2/pills/new/1.png'),
      sleep: getCompressedAssetUrl('/web_v2/uh-home-v2/pills/new/2.png'),
    }),
    [],
  );

  useEffect(() => {
    let isCancelled = false;
    const loaded: HTMLImageElement[] = new Array(FRAME_COUNT);

    const loadOne = (index: number) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.loading = 'eager';
        (img as any).fetchPriority = 'high';
        img.src = frameUrl(index);
        img.onload = img.onerror = () => {
          loaded[index - START_INDEX] = img;
          resolve();
        };
      });

    const CONCURRENCY = 8;
    let next = START_INDEX;
    let inflight = 0;

    const pump = () => {
      if (isCancelled) return;
      if (next > END_INDEX && inflight === 0) {
        imagesRef.current = loaded;
        setIsReady(true);
        return;
      }
      while (inflight < CONCURRENCY && next <= END_INDEX) {
        const current = next++;
        inflight++;
        loadOne(current).then(() => {
          inflight--;
          pump();
        });
      }
    };

    pump();

    return () => {
      isCancelled = true;
    };
  }, []);

  const dimensions = useMemo(() => {
    return { width: 1920, height: 1080 };
  }, []);

  const drawFrame = (frameIdx: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Skip if same frame as last drawn
    if (lastDrawnFrameRef.current === frameIdx) return;

    const ctx =
      ctxRef.current || canvas.getContext('2d', { desynchronized: true });
    if (!ctx) return;

    if (!ctxRef.current) {
      ctxRef.current = ctx;
    }

    const img = imagesRef.current[frameIdx];
    if (!img || !img.complete || img.naturalWidth === 0) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!drawImageSafe(ctx, img, 0, 0, canvas.width, canvas.height)) return;
    lastDrawnFrameRef.current = frameIdx;
  };

  useIsomorphicLayoutEffect(() => {
    if (!isReady || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const first = imagesRef.current[0];
    if (first && first.naturalWidth && first.naturalHeight) {
      canvas.width = first.naturalWidth;
      canvas.height = first.naturalHeight;
    } else {
      canvas.width = dimensions.width;
      canvas.height = dimensions.height;
    }
    drawFrame(0);
  }, [isReady, dimensions]);

  useIsomorphicLayoutEffect(() => {
    if (
      !isReady ||
      !sectionRef.current ||
      !canvasWrapperRef.current ||
      !headingRef.current ||
      imagesRef.current.length === 0
    ) {
      return;
    }

    gsapCtx.add(() => {
      const triggerEl = sectionRef.current;
      if (!triggerEl) return;
      const totalFrames = imagesRef.current.length - 1;
      const scrubObj = renderState.current;
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

      const tl = gsap.timeline({
        defaults: { duration: 1 },
        scrollTrigger: {
          immediateRender: false,
          trigger: triggerEl,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.3,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      const render = () => {
        const next = Math.max(0, Math.min(totalFrames, scrubObj.frame));
        drawFrame(next);
      };

      gsap.set(headingRef.current, { opacity: 0 });
      gsap.set(canvasWrapperRef.current, { scale: 1, y: -50 });
      pillsRef.current.forEach((pill) => {
        const marginStart = isMobile ? -2 : -4;
        gsap.set(pill, {
          opacity: 0,
          width: 0,
          marginLeft: marginStart,
          marginRight: marginStart,
        });
      });

      tl.addLabel('keyframe0', 0);

      // Mobile: shorter sequence animation
      const segmentADuration = isMobile ? 0.35 : 0.55;

      tl.fromTo(
        scrubObj,
        { frame: 0 },
        {
          frame: totalFrames,
          snap: 'frame',
          ease: 'none',
          onUpdate: render,
          duration: segmentADuration,
        },
        'keyframe0',
      );

      tl.to(
        headingRef.current,
        {
          opacity: 1,
          duration: segmentADuration * 0.6,
          ease: 'power2.out',
        },
        'keyframe0',
      );

      tl.to(
        canvasWrapperRef.current,
        {
          scale: 0.88,
          y: isMobile ? 5 : 0,
          duration: segmentADuration * 0.6,
          ease: 'power2.out',
        },
        'keyframe0+=0.2',
      );

      // Pill animations for both mobile and desktop
      tl.addLabel('keyframe1', segmentADuration);

      const segmentBDuration = 0.45;
      const pillTargetWidth = isMobile ? 56 : 112;
      const marginEnd = isMobile ? -10 : -20;

      pillsRef.current.forEach((pill, index) => {
        tl.to(
          pill,
          {
            opacity: 1,
            width: pillTargetWidth,
            marginLeft: marginEnd,
            marginRight: marginEnd,
            duration: segmentBDuration * 0.3,
            ease: 'back.out(1.7)',
          },
          `keyframe1+=${(index * segmentBDuration) / pillsRef.current.length}`,
        );
      });

      tl.addLabel('keyframe2', segmentADuration + segmentBDuration);

      // Dwell on the last frame (longer on mobile for better hold)
      const dwellDuration = isMobile ? 0.7 : 0.5;
      tl.to(
        scrubObj,
        {
          frame: totalFrames,
          snap: 'frame',
          ease: 'none',
          onUpdate: render,
          duration: dwellDuration,
        },
        'keyframe2',
      );

      // Register with the MasterTimeline for orchestration/debug overlay
      try {
        add(tl, 'home-device-view', 0);
      } catch (e) {
        // no-op
      }

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });
  }, [isReady, gsapCtx]);

  return (
    <Container
      ref={sectionRef}
      role="region"
      aria-label={t('homeDeviceView.container.ariaLabel.ultrahumanHomeDevice')}
    >
      <div className="pin-wrap">
        <div ref={headingRef} className="heading" aria-live="off">
          {(() => {
            pillsRef.current = [];
            const pill = (src: string) => (
              <Pill
                ref={(el) => {
                  if (el) pillsRef.current.push(el);
                }}
                $bg={src}
                aria-hidden="true"
              />
            );
            return HEADING_LINES.map((lineKey) => (
              <span key={lineKey} className="line">
                {t.rich(lineKey, {
                  home: () => pill(pillSrc.home),
                  sleep: () => pill(pillSrc.sleep),
                })}
              </span>
            ));
          })()}
        </div>
        <div ref={canvasWrapperRef} className="canvas-wrapper">
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={t('homeDeviceView.content.ariaLabel.ultrahumanHomeDevice')}
          />
        </div>
      </div>
    </Container>
  );
});

HomeDeviceView.displayName = 'HomeDeviceView';

export default HomeDeviceView;
